import React, { useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import Confetti from 'react-confetti';

const BonusRow = ({ bonus, index }) => {
    const [showConfetti, setShowConfetti] = useState(false);
    const confettiShownRef = useRef(false);
    const previousBonusValueRef = useRef(null);

    useEffect(() => {
        // Only show confetti if:
        // 1. Bonus value is greater than 0
        // 2. Confetti hasn't been shown yet for this session
        // 3. The bonus value has actually changed (not just a re-render)
        if (bonus?.value > 0 &&
            !confettiShownRef.current &&
            previousBonusValueRef.current !== bonus?.value) {

            confettiShownRef.current = true;
            previousBonusValueRef.current = bonus?.value;
            setShowConfetti(true);

            const timer = setTimeout(() => {
                setShowConfetti(false);
            }, 3000); // Show confetti for 3 seconds

            return () => clearTimeout(timer);
        }

        // Update the previous bonus value if it changed but conditions weren't met
        if (previousBonusValueRef.current !== bonus?.value) {
            previousBonusValueRef.current = bonus?.value;
        }
    }, [bonus]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: { xs: 1.5, md: 2 },
                position: "relative",
                padding: { xs: '2%', md: '1.5%' },
                background: 'transparent',
                overflow: 'visible',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
            }}
            className="animate-scale-in"
        >
            {showConfetti && (
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    numberOfPieces={1000}
                    recycle={false}
                />
            )}

            {/* Image with glow effect */}
            <Box
                sx={{
                    position: 'relative',
                    display: 'inline-block',
                }}
                className={bonus?.value === 0 || !bonus?.value ? "animate-sad-wobble" : ""}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '24vw', md: '18vw' },
                        height: { xs: '24vw', md: '18vw' },
                        maxWidth: '11rem',
                        maxHeight: '11rem',
                        borderRadius: '50%',
                        background: bonus?.value > 0
                            ? 'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, transparent 70%)'
                            : 'radial-gradient(circle, rgba(128, 128, 128, 0.2) 0%, transparent 70%)',
                        animation: bonus?.value > 0 ? 'pulse-glow 2s ease-in-out infinite' : 'fade-sad 2s ease-in-out infinite',
                        pointerEvents: 'none',
                    }}
                />
                <img
                    src="/img/items-icons/bonus.png"
                    alt="Bonus"
                    className={bonus?.value > 0 ? "animate-rotate-3d" : "animate-droop"}
                    style={{
                        width: 'clamp(6rem, 20vw, 9rem)',
                        height: 'clamp(6rem, 20vw, 9rem)',
                        filter: bonus?.value > 0
                            ? 'drop-shadow(0 0 1rem rgba(255, 215, 0, 0.6))'
                            : 'drop-shadow(0 0 0.5rem rgba(128, 128, 128, 0.4)) grayscale(60%)',
                        position: 'relative',
                        zIndex: 1,
                        margin: '0 auto',
                        display: 'block',
                    }}
                />
            </Box>

            <Typography
                sx={{
                    fontWeight: 900,
                    fontSize: { xs: "1.5rem", md: "1.5rem" },
                    fontFamily: "'Cinzel', serif",
                    color: '#FFD700',
                    textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.8), 0 0 0.8rem rgba(255, 215, 0, 0.8)',
                    textTransform: 'uppercase',
                    letterSpacing: { xs: '0.08rem', md: '0.1rem' },
                }}
            >
                🎁 Bonus
            </Typography>

            {/* Bonus Key */}
            <Box
                sx={{
                    padding: { xs: '1rem 1.6rem', md: '0.875rem 1.5rem' },
                    background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.8) 0%, rgba(101, 67, 33, 0.8) 100%)',
                    borderRadius: '0.75rem',
                    border: '0.15rem solid rgba(218, 165, 32, 0.8)',
                    width: 'auto',
                    maxWidth: '85%',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                        fontFamily: "'Philosopher', sans-serif",
                        color: '#FFA500',
                        textShadow: '0.15rem 0.15rem 0.3rem rgba(0,0,0,0.8)',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto',
                    }}
                >
                    {bonus?.key || "Bonus Key"}
                </Typography>
            </Box>

            {/* Bonus Value */}
            <Box
                sx={{
                    padding: { xs: '1.25rem 2rem', md: '1.125rem 1.875rem' },
                    background: 'linear-gradient(135deg, rgba(218, 165, 32, 0.3) 0%, rgba(184, 134, 11, 0.2) 100%)',
                    borderRadius: '1rem',
                    border: '0.2rem solid rgba(255, 215, 0, 0.5)',
                    boxShadow: '0 0 1.5rem rgba(218, 165, 32, 0.5), inset 0 0.15rem 0 rgba(255, 255, 255, 0.2)',
                    width: 'auto',
                    minWidth: { xs: '7rem', md: '6.5rem' },
                    maxWidth: '85%',
                }}
                className={bonus?.value > 0 ? "animate-pulse-glow" : ""}
            >
                <Typography
                    sx={{
                        fontWeight: 900,
                        fontSize: { xs: "2.5rem", md: "2.25rem" },
                        fontFamily: "'Cinzel', serif",
                        color: bonus?.value === 0 || !bonus?.value ? "#666" : '#FFF',
                        textShadow: bonus?.value === 0 || !bonus?.value ? 'none' : '0.25rem 0.25rem 0.5rem rgba(0,0,0,0.9), 0 0 1.25rem rgba(255, 215, 0, 1)',
                        background: bonus?.value === 0 || !bonus?.value ? 'none' : 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
                        WebkitBackgroundClip: bonus?.value === 0 || !bonus?.value ? 'unset' : 'text',
                        WebkitTextFillColor: bonus?.value === 0 || !bonus?.value ? '#666' : 'transparent',
                        backgroundClip: bonus?.value === 0 || !bonus?.value ? 'unset' : 'text',
                        filter: bonus?.value === 0 || !bonus?.value ? 'none' : 'drop-shadow(0 0.25rem 0.4rem rgba(0,0,0,0.5))',
                        lineHeight: 1,
                    }}
                    className={bonus?.value > 0 ? "animate-count-up" : ""}
                >
                    {bonus?.value === 0 || !bonus?.value ? "0" : `+${bonus?.value}`}
                </Typography>
            </Box>
        </Box>
    );
};

export default BonusRow;
